<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Locales } from '@/services/types/api.types';

const { t } = useI18n();

const props = defineProps<{
  title: string;
  analysisStatus: 'not_analyzed' | 'analyzing' | 'analyzed';
  analysisLanguage?: Locales;
  languageEnabled?: Locales[];
  isAnalyzing: boolean;
  isResetting: boolean;
}>();

const emit = defineEmits<{
  (e: 'analyze', language?: Locales): void;
  (e: 'reset'): void;
}>();

const statusIcon = computed(() => {
  switch (props.analysisStatus) {
    case 'analyzing':
      return 'hourglass_empty';
    case 'analyzed':
      return 'check_circle';
    default:
      return 'remove_circle_outline';
  }
});

const statusColor = computed(() => {
  switch (props.analysisStatus) {
    case 'analyzing':
      return 'primary';
    case 'analyzed':
      return 'positive';
    default:
      return 'grey';
  }
});

const statusLabel = computed(() => {
  const baseStatus = t(`consultation.ai.status.${props.analysisStatus}`);
  if (props.analysisStatus !== 'analyzed') {
    return baseStatus;
  }
  return `${baseStatus} · ${analysisLanguageLabel.value}`;
});

const reanalyzeLanguages = computed(() => {
  const enabled = props.languageEnabled ?? [];
  if (enabled.length === 0) return [];
  if (!props.analysisLanguage) return enabled;
  const current = enabled.includes(props.analysisLanguage) ? [props.analysisLanguage] : [];
  const others = enabled.filter((language) => language !== props.analysisLanguage);
  return [...current, ...others];
});

const analysisLanguageLabel = computed(() => {
  if (!props.analysisLanguage) return t('consultation.ai.analysisLanguage.unavailable');
  return t(`languages.${props.analysisLanguage}`);
});
</script>

<template>
  <div class="insights-header q-mb-md">
    <div class="row items-start">
      <div class="col text-h5 text-weight-medium">
        {{ title }}
      </div>
      <div class="col-auto q-ml-md">
        <div class="row items-center q-gutter-sm">
          <q-chip :icon="statusIcon" :color="statusColor" text-color="white" size="md">
            {{ statusLabel }}
          </q-chip>
          <q-spinner v-if="isAnalyzing" color="primary" size="sm" />
          <q-btn v-if="analysisStatus === 'analyzed'" flat dense round icon="more_vert" size="sm">
            <q-menu>
              <q-list dense style="min-width: 200px">
                <q-item
                  v-for="language in reanalyzeLanguages"
                  :key="language"
                  clickable
                  v-close-popup
                  @click="emit('analyze', language)"
                  :disable="isAnalyzing"
                >
                  <q-item-section avatar>
                    <q-icon name="refresh" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>
                      {{
                        t('consultation.ai.actions.reanalyzeIn', {
                          language: t(`languages.${language}`),
                        })
                      }}
                    </q-item-label>
                  </q-item-section>
                </q-item>
                <q-separator />
                <q-item clickable v-close-popup @click="emit('reset')" :disable="isResetting">
                  <q-item-section avatar>
                    <q-icon name="delete_forever" />
                  </q-item-section>
                  <q-item-section>
                    <q-item-label>{{ t('consultation.ai.actions.reset') }}</q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.insights-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 16px;
}
</style>
